import {
  ApprovalRuleStatus,
  formatDateAndTime,
  formatPriceAndCurrency,
  QuoteStatus,
  QuotesSubTabs,
  RuleStatusAsPerApiResponse,
  RuleStatusEquivalent
} from '@common/utils'
import {
  type ColumnCellTemplateData,
  MolsonBox,
  MolsonTypography
} from '@components/reusable'
import type { TFunction } from 'i18next'
import { WarningChip } from '../warning-chip/WarningChip'

type RenderPlainTextProps = {
  params: ColumnCellTemplateData
  key: string
  root?:
    | 'quotes'
    | 'customers'
    | 'rules'
    | 'manuallyAddedCustomers'
    | 'approvalRulesGrid'
  isDate?: boolean
  t?: TFunction
  gridTab?: QuotesSubTabs
  translationsKeyPrefix?: string
}

export const renderPlainTextColumn = ({
  params,
  key,
  root = 'quotes',
  isDate = false,
  t,
  gridTab,
  translationsKeyPrefix
}: RenderPlainTextProps) => {
  const manuallyAddedCustomersGrid = root === 'manuallyAddedCustomers'
  const potentialDuplicate =
    manuallyAddedCustomersGrid &&
    key === 'companyName' &&
    params.data.isDuplicate

  const quoteStatus = () => {
    if (root === 'rules') {
      return params.data.status === RuleStatusAsPerApiResponse.REVIEW_NEEDED
        ? RuleStatusEquivalent.REVIEW_NEEDED
        : RuleStatusEquivalent.ACTIVE
    }
    return t?.(`${translationsKeyPrefix}.${params.data.status}`)
  }

  const valueFormatters = {
    id: `QID-${params.data.id}`,
    specificationId: `SPECID-${params.data.id}`,
    ruleId: params.data.ruleId,
    approvalRuleId: `AWR-${params.data.id}`,
    price: formatPriceAndCurrency(params.data[key]),
    status: quoteStatus()
  }

  const getValue = () => {
    if (isDate) {
      return formatDateAndTime(params.data[key], { date: true, time: false })
    }
    return (
      valueFormatters[key as keyof typeof valueFormatters] ?? params.data[key]
    )
  }

  const isArchivedQuote = params.data.status === QuoteStatus.ARCHIVED
  const disabledState = isArchivedQuote && gridTab !== QuotesSubTabs.ARCHIVED
  const isError =
    (root === 'quotes' &&
      key === 'status' &&
      params.data.status === QuoteStatus.NEEDS_REVISING) ||
    (root === 'rules' &&
      key === 'status' &&
      params.data.status === RuleStatusAsPerApiResponse.REVIEW_NEEDED) ||
    (root === 'approvalRulesGrid' &&
      key === 'status' &&
      params.data.status === ApprovalRuleStatus.INACTIVE)

  const fontWeight = manuallyAddedCustomersGrid ? 700 : 400

  return potentialDuplicate ? (
    <MolsonBox
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}
    >
      <MolsonTypography
        variant="body2"
        whiteSpace="break-spaces"
        color={isError ? 'error' : ''}
        sx={{ opacity: disabledState ? 0.5 : 1 }}
        fontWeight={fontWeight}
      >
        {getValue()}
      </MolsonTypography>
      <WarningChip text={t?.('potentialDuplicate') ?? ''} />
    </MolsonBox>
  ) : (
    <MolsonTypography
      variant="body2"
      whiteSpace="break-spaces"
      color={isError ? 'error' : ''}
      sx={{ opacity: disabledState ? 0.5 : 1 }}
      fontWeight={fontWeight}
    >
      {getValue()}
    </MolsonTypography>
  )
}
